import {
	getOrdersOrderIdProtocolsPdfPath,
	type ProtocolDocumentNoteDto,
	type ProtocolDocumentResponseDto,
	type ProtocolDocumentStageDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { downloadFile } from "Infrastructure/Utilities/downloadFile";
import { useOrderProtocolDocumentQuery } from "Order/Modules/Protocol/Api/Query/useOrderProtocolDocumentQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import {
	CheckCircle,
	Download,
	RadioButtonUnchecked,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	type ChipProps,
	CircularProgress,
	Grid2,
	Stack,
	Typography,
} from "@mui/material";

type OrderProtocolWidgetProps = {
	orderId: number;
};

const protocolResource = Resources.base.order.protocol;

const badgeColor: Record<
	ProtocolDocumentResponseDto["badge"],
	ChipProps["color"]
> = {
	accepted: "success",
	autoAccepted: "info",
	pending: "warning",
	declined: "error",
};

const statusColor: Record<
	ProtocolDocumentStageDto["status"],
	ChipProps["color"]
> = {
	draft: "default",
	sent: "info",
	accepted: "success",
	declined: "error",
};

type ProtocolTileProps = {
	label: string;
	value: string;
	detail?: string | null;
};

function ProtocolTile({ label, value, detail }: ProtocolTileProps) {
	return (
		<Box
			sx={{
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				p: 2,
				height: "100%",
			}}
		>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="subtitle1">{value}</Typography>
			{detail && (
				<Typography variant="body2" color="text.secondary">
					{detail}
				</Typography>
			)}
		</Box>
	);
}

type ProtocolStageSectionProps = {
	title: string;
	stage: ProtocolDocumentStageDto;
};

function ProtocolStageSection({ title, stage }: ProtocolStageSectionProps) {
	const { t } = useResource();
	const { customer } = stage;

	// The generated customer DTO is flat: `kind` decides which of the optional
	// `atFormatted` / `name` fields are populated.
	const clientValue =
		customer.kind === "accepted"
			? (customer.atFormatted ?? t(protocolResource.notConfirmed))
			: customer.kind === "automatic"
				? t(protocolResource.automatic)
				: customer.kind === "declined"
					? t(protocolResource.declined)
					: t(protocolResource.pending);

	const clientDetail =
		customer.kind === "accepted"
			? customer.name
			: customer.kind === "automatic"
				? customer.atFormatted
				: customer.kind === "declined"
					? [customer.atFormatted, customer.name].filter(Boolean).join(" · ")
					: null;

	return (
		<BaseCard
			title={
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<Typography variant="h6">{title}</Typography>
					<Chip
						size="small"
						variant="outlined"
						color={statusColor[stage.status]}
						label={t(protocolResource.status[stage.status])}
					/>
				</Stack>
			}
		>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<ProtocolTile
						label={t(protocolResource.partner)}
						value={
							stage.partnerAcceptedAtFormatted ??
							t(protocolResource.notConfirmed)
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<ProtocolTile
						label={t(protocolResource.client)}
						value={clientValue}
						detail={clientDetail}
					/>
				</Grid2>
			</Grid2>
		</BaseCard>
	);
}

export function OrderProtocolWidget({ orderId }: OrderProtocolWidgetProps) {
	const { t } = useResource();
	const { data: protocol, isLoading } = useOrderProtocolDocumentQuery(orderId);

	const stageLabel: Record<ProtocolDocumentNoteDto["stage"], string> = {
		start: t(protocolResource.stageStart),
		handover: t(protocolResource.stageHandover),
	};

	const comments = (["start", "handover"] as ProtocolDocumentNoteDto["stage"][])
		.map((stage) => ({ stage, comment: protocol?.[stage]?.comment }))
		.filter(
			(
				item,
			): item is {
				stage: ProtocolDocumentNoteDto["stage"];
				comment: string;
			} => !!item.comment,
		);

	const header = (
		<Stack
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			flexWrap="wrap"
			gap={2}
		>
			<Stack flexDirection="row" alignItems="center" gap={1}>
				<Typography variant="h5">{t(protocolResource.title)}</Typography>
				{protocol && (
					<Chip
						size="small"
						variant="outlined"
						color={badgeColor[protocol.badge]}
						label={t(protocolResource.badge[protocol.badge])}
					/>
				)}
			</Stack>
			<Button
				variant="outlined"
				startIcon={<Download />}
				disabled={!protocol}
				onClick={() => downloadFile(getOrdersOrderIdProtocolsPdfPath(orderId))}
			>
				{t(protocolResource.downloadPdf)}
			</Button>
		</Stack>
	);

	if (isLoading) {
		return (
			<Stack gap={2}>
				{header}
				<Box width="100%" display="flex" justifyContent="center" mt={5}>
					<CircularProgress />
				</Box>
			</Stack>
		);
	}

	if (!protocol) {
		return (
			<Stack gap={2}>
				{header}
				<Typography color="text.secondary">
					{t(protocolResource.empty)}
				</Typography>
			</Stack>
		);
	}

	const hasAdjustments =
		!!protocol.originalEstimate || protocol.notes.length > 0;

	return (
		<Stack gap={2}>
			{header}

			<Typography color="text.secondary" fontWeight={600}>
				{`${t(protocolResource.partner)}: ${protocol.partnerName}`}
			</Typography>

			{(protocol.customerName || protocol.workAddress) && (
				<Stack flexDirection="row" flexWrap="wrap" gap={4}>
					{protocol.customerName && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t(protocolResource.client)}
							</Typography>
							<Typography variant="subtitle1">
								{protocol.customerName}
							</Typography>
						</Box>
					)}
					{protocol.workAddress && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t(protocolResource.address)}
							</Typography>
							<Typography variant="body1">{protocol.workAddress}</Typography>
						</Box>
					)}
				</Stack>
			)}

			<Grid2 container spacing={2}>
				{protocol.finalPrice && (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<BaseCard
							title={t(protocolResource.finalPrice)}
							sx={{ height: "100%" }}
						>
							<Typography variant="h4">
								{protocol.finalPrice.formatted}
							</Typography>
						</BaseCard>
					</Grid2>
				)}
				{hasAdjustments && (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<BaseCard
							title={t(protocolResource.priceAdjustments)}
							sx={{ height: "100%" }}
						>
							<Stack gap={0.5}>
								{protocol.originalEstimate && (
									<Typography variant="body2">
										{t(protocolResource.originalEstimate, {
											value: protocol.originalEstimate.formatted,
										})}
									</Typography>
								)}
								{protocol.notes.map((note, index) => (
									<Typography
										variant="body2"
										key={`${note.stage}-${index}`}
									>{`${stageLabel[note.stage]}: ${note.text}`}</Typography>
								))}
							</Stack>
						</BaseCard>
					</Grid2>
				)}
			</Grid2>

			{protocol.start && (
				<ProtocolStageSection
					title={t(protocolResource.jobStarted)}
					stage={protocol.start}
				/>
			)}
			{protocol.handover && (
				<ProtocolStageSection
					title={t(protocolResource.jobCompleted)}
					stage={protocol.handover}
				/>
			)}

			{protocol.checklist.length > 0 && (
				<BaseCard title={t(protocolResource.checklist)}>
					<Grid2 container spacing={1}>
						{protocol.checklist.map((item) => (
							<Grid2 key={item.key} size={{ xs: 12, sm: 6 }}>
								<Stack flexDirection="row" alignItems="center" gap={1}>
									{item.checked ? (
										<CheckCircle fontSize="small" color="primary" />
									) : (
										<RadioButtonUnchecked fontSize="small" color="disabled" />
									)}
									<Typography variant="body2">{item.label}</Typography>
								</Stack>
							</Grid2>
						))}
					</Grid2>
				</BaseCard>
			)}

			{comments.length > 0 && (
				<BaseCard title={t(protocolResource.clientComments)}>
					<Stack gap={1}>
						{comments.map((item) => (
							<Typography variant="body2" key={item.stage}>
								{comments.length > 1
									? `${stageLabel[item.stage]}: ${item.comment}`
									: item.comment}
							</Typography>
						))}
					</Stack>
				</BaseCard>
			)}
		</Stack>
	);
}
